<template>
  <div class="group-hero relative overflow-hidden rounded-2xl border border-secondary p-6 text-white">
    <div class="absolute inset-0 opacity-30">
      <div class="group-hero__orb group-hero__orb--primary"></div>
      <div class="group-hero__orb group-hero__orb--secondary"></div>
    </div>

    <div class="relative grid items-stretch gap-5 xl:grid-cols-[1fr_240px_1fr]">
      <div class="flex flex-col justify-between gap-5">
        <div class="rounded-2xl border border-white/10 bg-black/10 p-5 backdrop-blur">
          <div>
            <div class="mb-1 text-sm uppercase tracking-[0.18em] text-white/60">Revenue</div>
            <div class="text-4xl font-semibold leading-tight md:text-5xl">{{ formattedRevenue }}</div>
          </div>
        </div>

        <div class="grid gap-3 sm:grid-cols-2">
          <div class="rounded-xl border border-white/10 bg-white/10 p-4 backdrop-blur">
            <Icon
              type="handshake"
              class="mb-2 text-white/70"
            />
            <div class="text-2xl font-semibold">{{ totals.deals }}</div>
            <div class="mt-1 text-xs uppercase tracking-[0.14em] text-white/60">Deals</div>
          </div>
          <div
            v-for="metric in heroMetrics"
            :key="metric.label"
            v-tippy="metric.tooltip"
            class="rounded-xl border border-white/10 bg-white/10 p-4 backdrop-blur"
          >
            <Icon
              :type="metric.icon"
              class="mb-2 text-white/70"
            />
            <div class="text-2xl font-semibold">{{ metric.value }}</div>
            <div class="mt-1 text-xs uppercase tracking-[0.14em] text-white/60">{{ metric.label }}</div>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-center rounded-2xl border border-white/10 bg-white/10 p-5 backdrop-blur">
        <div
          class="group-hero__dial"
          :style="conversionRingStyle"
        >
          <div class="group-hero__dial-inner">
            <div class="text-center">
              <div class="text-4xl font-semibold">{{ formattedConversionRate }}</div>
              <div class="mt-1 text-xs uppercase tracking-[0.18em] text-white/60">Conversion</div>
            </div>
          </div>
        </div>
      </div>

      <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-1">
        <div class="relative overflow-hidden rounded-2xl border border-white/10 bg-black/20 p-5 backdrop-blur">
          <div class="absolute right-4 top-4 rotate-12 text-3xl text-white/20">
            <Icon type="crown" />
          </div>
          <div class="mb-4 flex items-center gap-4">
            <div class="relative">
              <Avatar
                :url="topSalesPerson?.user?.profilePicUrl ?? undefined"
                :text="topSalesPerson?.user?.initials"
                icon="user"
                size="2xl"
                no-border
              />
              <div class="group-hero__sales-badge">
                <Icon type="crown" />
              </div>
            </div>
            <div class="min-w-0">
              <div class="text-sm text-white/60">Top salesperson</div>
              <div class="truncate text-xl font-semibold">{{ topSalesPersonName }}</div>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div class="rounded-xl bg-white/10 p-3">
              <div class="text-2xl font-semibold">{{ topSalesPerson?.deals ?? 0 }}</div>
              <div class="text-xs uppercase tracking-[0.14em] text-white/50">Deals</div>
            </div>
            <div class="rounded-xl bg-white/10 p-3">
              <div class="text-2xl font-semibold">{{ formatCurrencyValue(topSalesPerson?.totalRevenue ?? 0) }}</div>
              <div class="text-xs uppercase tracking-[0.14em] text-white/50">Revenue</div>
            </div>
          </div>
        </div>

        <div class="relative overflow-hidden rounded-2xl border border-white/10 bg-white/10 p-5 backdrop-blur">
          <div class="absolute right-4 top-4 rotate-12 text-3xl text-white/20">
            <Icon type="trophy" />
          </div>
          <div class="flex h-full flex-col justify-between gap-5">
            <div>
              <div class="mb-3 inline-flex rounded-full border border-white/10 bg-white/10 p-3 text-xl">
                <Icon type="building" />
              </div>
            </div>
            <div>
              <div class="text-sm text-white/60">Best dealership</div>
              <div class="text-xl font-semibold">{{ bestDealership?.dealership.name ?? 'No dealership data' }}</div>
              <div class="mt-2 flex items-center gap-3 text-sm text-white/60">
                <span>{{ bestDealership?.stats.deals ?? 0 }} deals</span>
                <span>{{ formatCurrencyValue(bestDealership?.stats.totalRevenue ?? 0) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue'

// components
import Avatar from '@/components/avatar/Avatar.vue'
import Icon from '@/components/icon/Icon.vue'

// types
import type {
  GroupDashboardDealership,
  GroupDashboardSalesPerson,
  GroupDashboardTotals
} from '@/types/domain/groupDashboard'

// utilities
import { formatCurrency, formatNumber } from '@/utilities/number'

/** Props required to render the group dashboard hero. */
const props = defineProps({
  totals: {
    type: Object as PropType<GroupDashboardTotals>,
    required: true
  },
  bestDealership: {
    type: Object as PropType<GroupDashboardDealership | null>,
    default: null
  },
  topSalesPerson: {
    type: Object as PropType<GroupDashboardSalesPerson | null>,
    default: null
  }
})

/** Main revenue value formatted for the hero headline. */
const formattedRevenue = computed(() => formatCurrencyValue(props.totals.totalRevenue))

/** Conversion value displayed inside the hero dial. */
const formattedConversionRate = computed(() => `${formatNumber(props.totals.conversionRate * 100, 'normal', 1) ?? '0'}%`)

/** CSS variable style used to paint the conversion dial. */
const conversionRingStyle = computed(() => ({
  '--conversion-progress': `${Math.min(Math.max(props.totals.conversionRate * 100, 0), 100)}%`
}))

/** Salesperson display name with a graceful fallback for null API values. */
const topSalesPersonName = computed(() => props.topSalesPerson?.user?.name ?? 'No sales yet')

/** Compact metrics shown below the hero headline. */
const heroMetrics = computed(() => [
  {
    label: 'Answer rate',
    value: `${formatNumber(props.totals.answerRate * 100, 'normal', 1) ?? '0'}%`,
    tooltip: `Answer rate: ${formatNumber(props.totals.callsAnswered, 'normal') ?? '0'} answered calls`,
    icon: 'phone-volume'
  },
  {
    label: 'Booked',
    value: formatNumber(props.totals.appointmentsBooked, 'normal') ?? '0',
    tooltip: 'Appointments booked',
    icon: 'calendar-plus'
  },
  {
    label: 'Avg deal',
    value: formatCurrencyValue(props.totals.averageRevenuePerDeal),
    tooltip: 'Average revenue per deal',
    icon: 'sterling-sign'
  }
])

/**
 * Formats currency values for dashboard display.
 * @param value - Numeric revenue value from the API.
 * @returns A localized currency string.
 */
function formatCurrencyValue(value: number): string {
  return formatCurrency(value, 'GBP', 0) ?? '£0'
}
</script>

<style scoped>
.group-hero {
  border-color: rgba(255, 255, 255, 0.72);
  border-radius: 2rem;
  box-shadow: 0 24px 80px rgba(16, 24, 40, 0.08);
  background:
    radial-gradient(circle at top left, rgba(255, 255, 255, 0.26), transparent 28rem),
    radial-gradient(circle at bottom right, rgba(255, 255, 255, 0.14), transparent 24rem),
    linear-gradient(135deg, var(--utility-brand-700), var(--utility-purple-700) 48%, var(--utility-pink-700));
  background-clip: padding-box;
}

:global(body.dark .group-hero) {
  border-color: rgba(255, 255, 255, 0.1);
  background:
    radial-gradient(circle at top left, rgba(91, 63, 210, 0.34), transparent 30rem),
    radial-gradient(circle at bottom right, rgba(146, 38, 103, 0.24), transparent 24rem),
    linear-gradient(135deg, #140d27, #0c1020 56%, #070a14);
  background-clip: padding-box;
}

.group-hero__orb {
  position: absolute;
  border-radius: 9999px;
  filter: blur(12px);
}

.group-hero__orb--primary {
  right: 8%;
  top: -5rem;
  height: 16rem;
  width: 16rem;
  background: rgba(96, 165, 250, 0.45);
}

.group-hero__orb--secondary {
  bottom: -6rem;
  left: 36%;
  height: 18rem;
  width: 18rem;
  background: rgba(190, 24, 93, 0.36);
}

.group-hero__sales-badge {
  position: absolute;
  top: -0.5rem;
  right: -0.25rem;
  display: grid;
  width: 1.5rem;
  height: 1.5rem;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.34);
  border-radius: 9999px;
  background: linear-gradient(135deg, #fef3c7, #f59e0b);
  color: #78350f;
  font-size: 0.7rem;
  box-shadow: 0 8px 20px rgba(245, 158, 11, 0.24);
}

.group-hero__dial {
  display: grid;
  height: 13rem;
  width: 13rem;
  place-items: center;
  border-radius: 9999px;
  background: conic-gradient(#ffffff var(--conversion-progress), rgba(255, 255, 255, 0.18) 0);
}

.group-hero__dial-inner {
  display: flex;
  height: 9.5rem;
  width: 9.5rem;
  align-items: center;
  justify-content: center;
  place-items: center;
  border-radius: 9999px;
  background: #1f1a35;
  text-align: center;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.05);
}
</style>
